import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, type DirectionalText } from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import {
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  setButtonEnabled,
  TYPO,
  addDirectionalText,
} from "./uiTokens.js";
import { PALETTE } from "../themes/palette.js";
import { computeDungeonLayout } from "./dungeonLayout.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { TipCarouselView } from "./TipCarouselView.js";

export interface DungeonTabData {
  /** 현재 던전 표시 번호 (1-based). */
  readonly index: number;
  /** 현재 던전 보상 요약 (이미 현지화된 문자열). */
  readonly rewardSummary: string;
  /** 다음 자정까지 남은 ms (도전권 리셋 카운트다운). */
  readonly msUntilReset: number;
  readonly ticketsRemaining: number;
  readonly ticketLimit: number;
  readonly canChallenge: boolean;
  readonly canSweep: boolean;
}

export interface DungeonTabViewOptions {
  /** 도전 — 도전권 1 소모 후 현재 던전 전투 시작. */
  readonly onChallenge: () => void;
  /** 소탕 — 도전권 1 소모 후 마지막 클리어 던전 보상 즉시 수령. */
  readonly onSweep: () => void;
}

/** ms → "{h}시간 {m}분" / "{m}분" 현지화 표기. */
function formatTimeLeft(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? t("time.hm", { h, m }) : t("time.m", { m });
}

/**
 * 던전 탭 콘텐츠 — 현재 던전/보상/리셋 남은시간/도전권 + 도전·소탕 버튼.
 * 도전 = 도전권 1 소모 후 던전 전투 진행(클리어 시 보상 + 다음 던전), 소탕 = 이전 클리어 던전 보상 즉시 수령.
 * 소탕은 클리어한 던전이 1개 이상일 때만 활성(비활성 시 알파 0.4 회색). 배치는 dungeon.json + computeDungeonLayout.
 *
 * RTL: title/info는 `DirectionalText`(논리 정렬 "start" → LTR 좌/RTL 우)로 자동 미러. info는 멀티라인이라
 * `applyBidi`로 줄별 Bidi reorder까지 적용(아랍어 등 RTL 스크립트의 우→좌 글자 배열). 도전/소탕 버튼은 좌우 미러.
 */
export class DungeonTabView implements TabViewContract<DungeonTabData> {
  private readonly title: DirectionalText;
  /** 제목 아래 팁 캐러셀 (재사용 컴포넌트, tab="dungeon"). */
  private readonly tipView: TipCarouselView;
  private readonly info: DirectionalText;
  private readonly challengeBtn: NineSliceButton;
  private readonly sweepBtn: NineSliceButton;
  private readonly rect: { x: number; y: number; width: number };

  constructor(scene: Phaser.Scene, x: number, y: number, width: number, opts: DungeonTabViewOptions) {
    this.rect = { x, y, width };
    const L = computeDungeonLayout({ x, y, width, height: 0 }, getTextDirection());
    // 단일 라인 제목 — 정렬만 방향 인지(글자 1단어라 Bidi 불필요).
    this.title = addDirectionalText(scene, L.title.x, L.title.y, t("dungeon.title"), {
      fontFamily: FONT_FAMILY,
      fontSize: TYPO.label,
      color: PALETTE.textSecondary,
    });
    this.title.setOrigin(this.title.originX, 0).setDepth(2);
    // 제목 아래 팁 캐러셀 — 던전 설명(2~3개 팁)을 랜덤 시작 + ◀/▶ 순환(재사용 컴포넌트, 전체폭).
    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "dungeon");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);
    // 멀티라인 정보 — 정렬 + 줄별 Bidi reorder(applyBidi). DirectionalText 세로 origin은 상단(0)으로 보정.
    this.info = addDirectionalText(
      scene,
      L.info.x,
      L.info.y,
      "",
      {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
        lineSpacing: 4,
        wordWrap: { width: L.info.width },
      },
      "start",
      true,
    );
    this.info.setOrigin(this.info.originX, 0).setDepth(2);
    this.challengeBtn = new NineSliceButton(scene, L.challenge.x, L.challenge.y, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("dungeon.challenge"),
      widthOverride: 140,
      heightOverride: 34,
    });
    this.challengeBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onChallenge());
    this.sweepBtn = new NineSliceButton(scene, L.sweep.x, L.sweep.y, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("dungeon.sweep"),
      widthOverride: 140,
      heightOverride: 34,
    });
    this.sweepBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onSweep());
    scene.add.existing(this.challengeBtn);
    scene.add.existing(this.sweepBtn);
  }

  update(d: DungeonTabData): void {
    this.info.setLogicalText(
      [
        t("dungeon.index", { index: d.index }),
        t("dungeon.reward", { reward: d.rewardSummary }),
        t("dungeon.tickets", { tickets: d.ticketsRemaining, limit: d.ticketLimit }),
        t("dungeon.timeLeft", { time: formatTimeLeft(d.msUntilReset) }),
      ].join("\n"),
    );
    setButtonEnabled(this.challengeBtn, d.canChallenge);
    setButtonEnabled(this.sweepBtn, d.canSweep);
  }

  /** 언어 변경 — 정적 제목/버튼만 갱신 (info는 update()로). */
  applyLanguage(): void {
    this.title.setLogicalText(t("dungeon.title"));
    this.tipView.applyLanguage();
    this.challengeBtn.setLabel(t("dungeon.challenge"));
    this.sweepBtn.setLabel(t("dungeon.sweep"));
  }

  /** 방향(LTR↔RTL) 전환 — 텍스트 정렬/Bidi + 버튼 좌우 미러 재배치. */
  relayout(dir: TextDirection): void {
    const L = computeDungeonLayout({ ...this.rect, height: 0 }, dir);
    this.title.setPosition(L.title.x, L.title.y).setDirection(dir);
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    this.info.setPosition(L.info.x, L.info.y).setDirection(dir).setWordWrapWidth(L.info.width);
    this.challengeBtn.setPosition(L.challenge.x, L.challenge.y);
    this.sweepBtn.setPosition(L.sweep.x, L.sweep.y);
  }

  /** 셸 펼치기/접기 — 콘텐츠 rect(상단) 갱신 후 relayout으로 전체 재배치(고정 콘텐츠라 새 패널 상단에 정렬). */
  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.relayout(getTextDirection());
  }

  setVisible(visible: boolean): void {
    this.title.setVisible(visible);
    this.tipView.setVisible(visible);
    this.info.setVisible(visible);
    this.challengeBtn.setVisible(visible);
    this.sweepBtn.setVisible(visible);
  }
}
